import React, { useRef, useEffect } from 'react';

/**
 * Декоративный фон экрана входа.
 *
 * Ранее здесь была canvas-анимация «матричного дождя» (requestAnimationFrame,
 * захардкоженные тёмные цвета). Она игнорировала prefers-reduced-motion,
 * постоянно нагружала CPU и конфликтовала со светлой темой.
 *
 * Теперь фон статичный и рисуется в тон текущей палитре: точечная сетка
 * цветом --color-border и мягкое свечение цветом --color-primary.
 * Кадр перерисовывается только при resize и смене темы — анимации нет,
 * поэтому требование prefers-reduced-motion выполняется автоматически
 * (все пользователи получают статичный кадр).
 */

// Преобразует hex-токен (#rgb / #rrggbb) в rgba с заданной прозрачностью.
function withAlpha(hex, alpha) {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
  const num = parseInt(full, 16);
  if (Number.isNaN(num) || full.length !== 6) return `rgba(30, 64, 175, ${alpha})`;
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function MatrixRain() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext?.('2d');
    if (!ctx) return undefined;

    // Проверка оставлена осознанно: фон статичен, но если сюда вернётся
    // анимация — при reduce она запускаться не должна.
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

    const draw = () => {
      const styles = getComputedStyle(document.documentElement);
      const border = styles.getPropertyValue('--color-border').trim() || '#dbeafe';
      const primary = styles.getPropertyValue('--color-primary').trim() || '#1e40af';

      const dpr = window.devicePixelRatio || 1;
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      // Точечная сетка в тон границ.
      const gap = 28;
      ctx.fillStyle = withAlpha(border, 0.7);
      for (let x = gap / 2; x < w; x += gap) {
        for (let y = gap / 2; y < h; y += gap) {
          ctx.beginPath();
          ctx.arc(x, y, 1, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Мягкое свечение primary-цвета сверху.
      const glow = ctx.createRadialGradient(w / 2, 0, 0, w / 2, 0, Math.max(w, h) * 0.85);
      glow.addColorStop(0, withAlpha(primary, 0.08));
      glow.addColorStop(1, withAlpha(primary, 0));
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, w, h);

      // reducedMotion.matches здесь ничего не меняет: кадр и так статичный.
      void reducedMotion;
    };

    draw();
    window.addEventListener('resize', draw);

    // Перерисовка при смене темы (переключение класса/атрибута на <html>).
    const observer = new MutationObserver(draw);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-bs-theme', 'data-theme'],
    });

    const scheme = window.matchMedia?.('(prefers-color-scheme: dark)');
    scheme?.addEventListener?.('change', draw);

    return () => {
      window.removeEventListener('resize', draw);
      observer.disconnect();
      scheme?.removeEventListener?.('change', draw);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true" />;
}
